import { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchAnnualPerformance, fetchStrategiesForPeriod, fetchAllStrategies } from "../../../../utils/restAPI";
import { transformAnnualPerformanceData } from "../../../../utils/transformers";
import {
  selectDetailPeriod,
  selectDetailStrategies,
  setDetailStrategies,
  setDetailAnnualPerformance,
  setAnnualPerformanceLoading,
  setDetailLoading,
  setDetailPeriod,
  setOwnerStrategies,
  clearStrategyDetail,
} from "../../../../Redux/Reducers/strategyStoreSlice";
import { selectStrategies } from "../../../../Redux/Reducers/strategiesSlice";
import {
  transformStrategyData,
  withPeriodCached,
  applyPeriod,
  storePeriod,
} from "../strategyTransformers";

const useStrategyDetail = (slug) => {
  const dispatch = useDispatch();
  const period = useSelector(selectDetailPeriod);
  const strategies = useSelector(selectDetailStrategies);
  const seedStrategies = useSelector(selectStrategies) || [];

  const seedStrategy = seedStrategies.find((s) => s.metadata?.slug === slug);
  const [resolvedId, setResolvedId] = useState(null);
  const [resolvedOwnerEmail, setResolvedOwnerEmail] = useState(null);

  const mainStrategyId = seedStrategy?.strategyId || resolvedId;
  const mainStrategyOwnerEmail = seedStrategy?.owner || resolvedOwnerEmail;

  // When seed data is not available (unauthenticated) or the slug has changed.
  useEffect(() => {
    let cancelled = false;

    setResolvedId(null);
    setResolvedOwnerEmail(null);
    dispatch(clearStrategyDetail());

    if (seedStrategy) return;

    const resolveBySlug = async () => {
      dispatch(setDetailLoading(true));
      try {
        const response = await fetchAllStrategies();
        if (cancelled) return;
        if (response.success) {
          const found = response.data.find((s) => s.slug === slug);
          if (found) {
            setResolvedId(found.strategyId);
            if (found.owner) setResolvedOwnerEmail(found.owner.id);
          } else {
            dispatch(setDetailLoading(false));
          }
        } else {
          dispatch(setDetailLoading(false));
        }
      } catch (error) {
        if (cancelled) return;
        console.error("Failed to resolve strategy by slug:", error);
        dispatch(setDetailLoading(false));
      }
    };

    resolveBySlug();
    return () => { cancelled = true; };
  }, [slug]); // eslint-disable-line react-hooks/exhaustive-deps

  // First load: fetch main strategy once its ID is resolved from seed data
  useEffect(() => {
    if (!mainStrategyId) return;
    const fetchMainStrategy = async () => {
      dispatch(setDetailLoading(true));
      try {
        const response = await fetchStrategiesForPeriod(period, 1, 1, "", "owner", mainStrategyId, mainStrategyOwnerEmail);
        if (response.success && response.data.length > 0) {
          const transformed = transformStrategyData(response.data);
          dispatch(setDetailStrategies([withPeriodCached(transformed[0], period)]));
          if (response.ownerStrategies?.length > 0) {
            dispatch(setOwnerStrategies(transformStrategyData(response.ownerStrategies)));
          }
        }
      } catch (error) {
        console.error("Failed to fetch main strategy:", error);
      } finally {
        dispatch(setDetailLoading(false));
      }
    };
    fetchMainStrategy();
  }, [mainStrategyId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch annual performance when main strategy is first loaded
  useEffect(() => {
    const mainStrategy = strategies[0];
    if (!mainStrategy?.idStrategy) return;
    const fetchData = async () => {
      dispatch(setAnnualPerformanceLoading(true));
      try {
        const response = await fetchAnnualPerformance(mainStrategy.idStrategy);
        if (response.success && response.data) {
          dispatch(setDetailAnnualPerformance(transformAnnualPerformanceData(response.data)));
        }
      } catch (error) {
        console.error("Failed to fetch annual performance:", error);
      } finally {
        dispatch(setAnnualPerformanceLoading(false));
      }
    };
    fetchData();
  }, [strategies[0]?.idStrategy]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      dispatch(clearStrategyDetail());
    };
  }, [dispatch]);

  const handlePeriodChange = async (event) => {
    const newPeriod = event.target.value;
    dispatch(setDetailPeriod(newPeriod));

    // For custom period, wait for the user to supply dates via handleCustomFetch
    if (newPeriod === "custom") return;

    if (strategies.length === 0) return;

    const uncachedStrategies = strategies.filter((s) => !s.fetchedPeriods?.[newPeriod]);

    if (uncachedStrategies.length === 0) {
      dispatch(setDetailStrategies(strategies.map((s) => applyPeriod(s, newPeriod))));
      return;
    }

    dispatch(setDetailLoading(true));
    try {
      const uncachedIds = uncachedStrategies.map((s) => s.idStrategy);
      const response = await fetchStrategiesForPeriod(newPeriod, 1, uncachedIds.length, "", "owner", uncachedIds, mainStrategyOwnerEmail);
      if (response.success) {
        const transformed = transformStrategyData(response.data);
        const fetchedById = Object.fromEntries(transformed.map((s) => [s.idStrategy, s]));
        dispatch(
          setDetailStrategies(
            strategies.map((s) => {
              if (s.fetchedPeriods?.[newPeriod]) return applyPeriod(s, newPeriod);
              const freshData = fetchedById[s.idStrategy];
              return freshData ? storePeriod(s, newPeriod, freshData) : s;
            })
          )
        );
      }
    } catch (error) {
      console.error("Failed to fetch strategies for period:", error);
    } finally {
      dispatch(setDetailLoading(false));
    }
  };

  const handleCustomFetch = async (startDate, endDate) => {
    if (!startDate || !endDate || strategies.length === 0) return;

    dispatch(setDetailLoading(true));
    try {
      const ids = strategies.map((s) => s.idStrategy);
      const response = await fetchStrategiesForPeriod("custom", 1, ids.length, "", "owner", ids, mainStrategyOwnerEmail, startDate, endDate);
      if (response.success) {
        const transformed = transformStrategyData(response.data);
        dispatch(setDetailStrategies(transformed));
      }
    } catch (error) {
      console.error("Failed to fetch strategies for custom period:", error);
    } finally {
      dispatch(setDetailLoading(false));
    }
  };

  return {
    handlePeriodChange,
    handleCustomFetch,
  };
};

export default useStrategyDetail;
